import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { bookAPI } from '../../../../utils/api';
import BookCreate from '../../create/js/bookCreate';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';

export default function BookTable({ onCreate, reloadKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [colFilter, setColFilter] = useState({ medium: '', size: '' });
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await bookAPI.getAll();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [reloadKey]);

  const remove = async (id) => {
    const res = await Swal.fire({
      title: 'Delete Book?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#e74c3c',
    });
    if (res.isConfirmed) {
      try {
        await bookAPI.delete(id);
        await load();
        Swal.fire({ icon: 'success', title: 'Deleted', toast: true, position: 'top-end', timer: 3000 });
      } catch (e) {
        Swal.fire({
          icon: 'error',
          title: 'Book could not be deleted',
          text: e.response?.data?.error || e.response?.data?.message || e.message,
        });
      }
    }
  };

  const exportCsv = async () => {
    try {
      const res = await bookAPI.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'books_export.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { Swal.fire({ icon:'error', title:'Error', text:e.message }); }
  };

  const importCsv = async (csv) => {
    const res = await bookAPI.importCsv(csv);
    await load();
    return res.data;
  };

  const downloadSample = async () => {
    try {
      const res = await bookAPI.sampleCsv();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'books_sample.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'book_code', header: 'Code' },
    { accessorKey: 'name', header: 'Book Title', cell: ({ getValue }) => <span style={{ fontWeight: 600 }}>{getValue()}</span> },
    { accessorKey: 'medium', header: 'Medium' },
    { accessorKey: 'total_pages', header: 'Pages', cell: ({ getValue }) => <span style={{ color: '#0f6fad' }}>{getValue() || '-'}</span> },
    { accessorKey: 'color_scheme', header: 'Color' },
    { accessorKey: 'size', header: 'Size' },
    { accessorKey: 'binding', header: 'Binding' },
    { accessorKey: 'price', header: 'Price', cell: ({ getValue }) => getValue() ? `Rs. ${getValue()}` : '-' },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view" onClick={() => setViewRow(row.original)}><FaEye /></button>
          <button className="eduss-global-action-btn edit" onClick={() => setEditRow(row.original)}><FaEdit /></button>
          <button className="eduss-global-action-btn delete" onClick={() => remove(row.original.id)}><FaTrash /></button>
        </div>
      ),
    },
  ];

  const filteredData = rows.filter(r => {
    const f = filter.toLowerCase();
    return (!filter || r.name?.toLowerCase().includes(f) || r.book_code?.toLowerCase().includes(f))
    && (!colFilter.medium || r.medium === colFilter.medium)
    && (!colFilter.size || r.size === colFilter.size);
  });

  const mediums = [...new Set(rows.map(r => r.medium).filter(Boolean))].map(m => ({ label: m, value: m }));
  const sizes = [...new Set(rows.map(r => r.size).filter(Boolean))].map(s => ({ label: s, value: s }));

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar 
        title="Books Management"
        subtitle="View and manage Sindh Text Book Board publication inventory"
        onAdd={onCreate}
        onExport={exportCsv}
        onImport={importCsv}
        onDownloadSample={downloadSample}
        onSearch={setFilter}
        onFilterChange={(name, value) => setColFilter(prev => ({ ...prev, [name]: value }))}
        filterOptions={[
          { name: 'medium', value: colFilter.medium, options: mediums },
          { name: 'size', value: colFilter.size, options: sizes }
        ]}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
          enableSelection={true}
          bulkActions={[
            { label: 'Delete Selected', icon: <FaTrash />, className: 'delete', onClick: (rows) => console.log('Bulk delete', rows) }
          ]}
        />
      </div>

      {editRow && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Update Book</h3>
              <button onClick={() => setEditRow(null)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <BookCreate book={editRow} onClose={() => setEditRow(null)} onSubmit={async () => { await load(); setEditRow(null); }} />
            </div>
          </div>
        </div>,
        document.body
      )}

      {viewRow && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setViewRow(null)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Book Details</h3>
              <button onClick={() => setViewRow(null)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <table className="mini-info-table">
                <tbody>
                  <tr><th>ID</th><td>{viewRow.id}</td></tr>
                  <tr><th>Book Code</th><td>{viewRow.book_code || '-'}</td></tr>
                  <tr><th>Title</th><td>{viewRow.name}</td></tr>
                  <tr><th>Medium</th><td>{viewRow.medium || '-'}</td></tr>
                  <tr><th>Pages (1st Jacket)</th><td>{viewRow.pages_first_jacket || '-'}</td></tr>
                  <tr><th>Pages (2nd Jacket)</th><td>{viewRow.pages_second_jacket || '-'}</td></tr>
                  <tr><th>Title Pages</th><td>{viewRow.title_pages || '-'}</td></tr>
                  <tr><th>Total Pages</th><td>{viewRow.total_pages || '-'}</td></tr>
                  <tr><th>Color Scheme</th><td>{viewRow.color_scheme || '-'}</td></tr>
                  <tr><th>Size</th><td>{viewRow.size || '-'}</td></tr>
                  <tr><th>Binding</th><td>{viewRow.binding || '-'}</td></tr>
                  <tr><th>Unit Price</th><td>{viewRow.price ? `Rs. ${viewRow.price}` : '-'}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
BookTable.propTypes = {
  onCreate: PropTypes.func,
  reloadKey: PropTypes.number
};
